import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import cx from 'clsx';

import { fromCentsWithLocale } from 'utils/Currency';
import { getSurchargeAmountForTotal, getSurchargePercentageString } from 'utils/Surcharges';
import { useCheckoutCurrency } from 'hooks/context/checkoutSession';
import { useCalcContext } from 'hooks/context/calc';

import { VariableTotal } from './VariableTotal';
import { Deductions } from './Deductions';

import { TOTALS_TEST_IDS } from './const';

import styles from './styles.module.scss';


export type TotalsProps = {
  subtotal?: number;
  taxAmount?: number;
  total: number;
  tip?: number;
};

export const Totals: FC<TotalsProps> = ({ subtotal, total, taxAmount, tip }) => {
  const { t } = useTranslation();
  const { surchargeRate, convenienceFee, isVariableAmount, pendingAmount } = useCalcContext();
  const currency = useCheckoutCurrency();

  return (
    <div>
      {isVariableAmount && <VariableTotal />}

      <div className={styles.totalsWrapper}>
        {subtotal !== undefined && total !== subtotal && !isVariableAmount && (
          <p data-testid={TOTALS_TEST_IDS.subtotal}>
            <Trans i18nKey="subtotalWithAmount">
              <span className={styles.subtotal}>Subtotal</span>
              <span className={styles.amount}>
                {{ subtotal: t('money', { amount: fromCentsWithLocale(subtotal, currency) }) }}
              </span>
            </Trans>
          </p>
        )}

        {!!tip && (
          <p data-testid={TOTALS_TEST_IDS.tip}>
            <Trans i18nKey="tipWithAmount">
              <span className={styles.tax}>Tip</span>
              <span className={styles.amount}>
                {{ tip: t('money', { amount: fromCentsWithLocale(tip, currency) }) }}
              </span>
            </Trans>
          </p>
        )}

        {taxAmount !== undefined && (
          <p data-testid={TOTALS_TEST_IDS.taxAmount}>
            <Trans i18nKey="taxWithAmount">
              <span className={styles.tax}>Tax</span>
              <span className={styles.amount}>
                {{ tax: t('money', { amount: fromCentsWithLocale(taxAmount, currency) }) }}
              </span>
            </Trans>
          </p>
        )}

        {!!convenienceFee && (
          <p data-testid={TOTALS_TEST_IDS.convenienceFee}>
            <Trans i18nKey="convenienceFeeWithAmount">
              <span className={styles.tax}>Convenience fee</span>
              <span className={styles.amount}>
                {{ convenienceFee: t('money', { amount: fromCentsWithLocale(convenienceFee, currency) }) }}
              </span>
            </Trans>
          </p>
        )}

        <Deductions />

        <p data-testid={TOTALS_TEST_IDS.total}>
          <Trans i18nKey="totalWithAmount">
            <span className={styles.total}>Total</span>
            <span className={cx(styles.total, styles.amount)}>
              {{ total: t('money', { amount: fromCentsWithLocale(pendingAmount, currency) }) }}
            </span>
          </Trans>
        </p>

        {surchargeRate !== undefined &&
        <p data-testid={TOTALS_TEST_IDS.surcharge}>
          <Trans i18nKey="surchargeAmount">
            <span className={styles.surcharge}>{{ rate: getSurchargePercentageString(surchargeRate) }}% credit card surcharge</span>
            <span className={cx(styles.surcharge, styles.amount)}>
              {{ surchargeAmount: t('money', { amount: fromCentsWithLocale(getSurchargeAmountForTotal(surchargeRate, pendingAmount), currency) }) }}
            </span>
          </Trans>
        </p>
        }
      </div>
    </div>
  );
};
